import { Document, Font, pdf } from '@react-pdf/renderer';
import { ReactElement } from 'react';
import {
  PdfMediaResolverProvider,
  type PdfMediaResolver,
} from './AssessmentReport/usePdfMediaResolver';
import EducationVerification from './EducationVerification';
import ProgressReport from './ProgressReport';
import AttendanceReport from './AttendanceReport';
import StatementOfAttainment from './StatementOfAttainment';
import CertificateAndRecordOfResults from './CertificateAndRecordOfResults';
import EducationVerificationProps from './EducationVerification/types';
import ProgressReportProps from './ProgressReport/types';
import AttendanceReportProps from './AttendanceReport/types';
import StatementOfAttainmentProps from './StatementOfAttainment/types';
import CertificateAndRecordOfResultsProps from './CertificateAndRecordOfResults/types';
import NonVocationalCertificate from './NonVocationalCertificate';
import NonVocationalCertificateProps from './NonVocationalCertificate/types';
import CourseCatalogProps from './CourseCatalog/types';
import CourseCatalog from './CourseCatalog';
import ConfirmationOfEnrolment from './ConfirmationOfEnrolment';
import ConfirmationOfEnrolmentProps from './ConfirmationOfEnrolment/types';
import AssessmentReport from './AssessmentReport';
import AssessmentAuditReportProps from './AssessmentReport/types';
import StudentTimesheet from './StudentTimesheet';
import StudentTimesheetProps from './StudentTimesheet/types';

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

Font.register({
  family: 'Inter',
  src: `${baseUrl}/public/fonts/Inter-Regular.ttf`,
  fontStyle: 'normal',
  fontWeight: 'normal',
});

Font.register({
  family: 'Inter-SemiBold',
  src: `${baseUrl}/public/fonts/Inter-SemiBold.ttf`,
  fontStyle: 'normal',
  fontWeight: 'semibold',
});

// Break lines on whole words only. react-pdf's default hyphenation splits words
// mid-word; returning the word unbroken keeps wraps at spaces.
Font.registerHyphenationCallback((word) => [word]);

interface DocumentPropsMap {
  educationVerification: EducationVerificationProps;
  progressReport: ProgressReportProps;
  attendanceReport: AttendanceReportProps;
  statementOfAttainment: StatementOfAttainmentProps;
  certificateAndRecordOfResults: CertificateAndRecordOfResultsProps;
  nonVocationalCertificate: NonVocationalCertificateProps;
  courseCatalog: CourseCatalogProps;
  confirmationOfEnrolment: ConfirmationOfEnrolmentProps;
  assessmentReport: AssessmentAuditReportProps;
  studentTimesheet: StudentTimesheetProps;
}

type DocumentComponentMap = {
  [K in keyof DocumentPropsMap]: (props: { data: DocumentPropsMap[K] }) => ReactElement | null;
};

const documentComponents: DocumentComponentMap = {
  educationVerification: EducationVerification,
  progressReport: ProgressReport,
  attendanceReport: AttendanceReport,
  statementOfAttainment: StatementOfAttainment,
  certificateAndRecordOfResults: CertificateAndRecordOfResults,
  nonVocationalCertificate: NonVocationalCertificate,
  courseCatalog: CourseCatalog,
  confirmationOfEnrolment: ConfirmationOfEnrolment,
  assessmentReport: AssessmentReport,
  studentTimesheet: StudentTimesheet,
};

export type DocumentType = keyof DocumentComponentMap;

type ClientOnlyDocumentType = 'studentTimesheet';

export type BackendDocumentType = Exclude<DocumentType, ClientOnlyDocumentType>;

export type DocumentComponentProps = DocumentPropsMap[DocumentType];

export type BackendDocumentComponentProps = DocumentPropsMap[BackendDocumentType];

/**
 * Render a PDF document component to a Blob.
 * Calls the component as a function to avoid JSX generic inference issues
 * with React's LibraryManagedAttributes and mapped types.
 */
async function generatePdfBlob<T extends DocumentType>(
  type: T,
  data: DocumentPropsMap[T],
  mediaResolver?: PdfMediaResolver,
): Promise<Blob | null> {
  const Component = documentComponents[type];

  if (Component == null) {
    return null;
  }

  const docComponent = Component({ data });
  const tree =
    mediaResolver != null ? (
      <PdfMediaResolverProvider resolver={mediaResolver}>{docComponent}</PdfMediaResolverProvider>
    ) : (
      docComponent
    );
  const doc = pdf(<Document>{tree}</Document>);
  return doc.toBlob();
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',').slice(1).join(','));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Generate a PDF and open it in a new browser tab.
 *
 * In a React Native WebView, the PDF is sent to the native shell via
 * postMessage because window.open() is not supported.
 *
 * Callers with async work before this function (e.g. data fetching) should
 * pre-open the window during the user gesture and pass it as `targetWindow`
 * to avoid popup blockers.
 */
export async function openPdfInNewTab<T extends DocumentType>(
  type: T,
  data: DocumentPropsMap[T],
  targetWindow?: Window | null,
  mediaResolver?: PdfMediaResolver,
): Promise<void> {
  const blob = await generatePdfBlob(type, data, mediaResolver);

  if (blob == null) {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.close();
    }
    return;
  }

  if (window.ReactNativeWebView?.postMessage) {
    const base64 = await blobToBase64(blob);
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'pdf', base64, filename: `${type}.pdf` }),
    );
    return;
  }

  const win = targetWindow ?? window.open('', '_blank');
  if (!win || win.closed) {
    return;
  }

  const url = URL.createObjectURL(blob);
  win.location.replace(url);
}

/**
 * Generate a PDF and trigger a file download with the given filename.
 */
export async function downloadPdfAsFile<T extends DocumentType>(
  type: T,
  data: DocumentPropsMap[T],
  filename: string,
  mediaResolver?: PdfMediaResolver,
): Promise<void> {
  const blob = await generatePdfBlob(type, data, mediaResolver);

  if (blob == null) {
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
